import { parseNbtFile, type NbtCompound } from "./nbt.ts";
import type { FileSource, WorldLayout } from "./source.ts";

export interface LevelInfo {
  name: string;
  version: string; // "26.2"
  dataVersion: number;
  seed?: string;
  spawn?: [number, number, number];
  modded: boolean;
  brands: string[];
  lastPlayed?: number;
  /** overworld time of day in ticks (0 sunrise · 6000 noon · 12000 sunset · 18000 midnight) */
  dayTime?: number;
}

/** Reads level.dat (+ 26.x data/minecraft/world_gen_settings.dat for the seed). */
export async function readLevel(src: FileSource, layout: WorldLayout): Promise<LevelInfo> {
  const nbt = await parseNbtFile(await src.read(layout.root + "level.dat"));
  const d = (nbt.Data as NbtCompound) ?? nbt;
  const ver = (d.Version as NbtCompound) ?? {};
  let seed: string | undefined;
  const wgs = d.WorldGenSettings as NbtCompound | undefined;
  if (wgs && wgs.seed !== undefined) seed = String(wgs.seed);
  else if (d.RandomSeed !== undefined) seed = String(d.RandomSeed);
  const wgsPath = layout.root + "data/minecraft/world_gen_settings.dat";
  if (seed === undefined && src.paths().includes(wgsPath)) {
    try {
      const w = await parseNbtFile(await src.read(wgsPath));
      const s = (w.data as NbtCompound)?.seed;
      if (s !== undefined) seed = String(s);
    } catch { /* ignore */ }
  }
  // time of day: 26.x keeps it in data/minecraft/world_clocks.dat, older versions in level.dat
  const num = (v: unknown) => (typeof v === "number" ? v : typeof v === "bigint" ? Number(v % 24000n) : undefined);
  let dayTime = num(d.DayTime);
  const clocksPath = layout.root + "data/minecraft/world_clocks.dat";
  if (dayTime === undefined && src.paths().includes(clocksPath)) {
    try {
      const c = await parseNbtFile(await src.read(clocksPath));
      const ow = ((c.data as NbtCompound)?.["minecraft:overworld"] as NbtCompound | undefined)?.total_ticks;
      dayTime = num(ow);
    } catch { /* ignore */ }
  }
  if (dayTime !== undefined) dayTime = ((dayTime % 24000) + 24000) % 24000;
  let spawn: [number, number, number] | undefined;
  const sp = d.spawn as NbtCompound | undefined;
  if (sp && sp.pos instanceof Int32Array) spawn = [sp.pos[0], sp.pos[1], sp.pos[2]];
  else if (typeof d.SpawnX === "number") spawn = [d.SpawnX as number, d.SpawnY as number, d.SpawnZ as number];
  return {
    name: String(d.LevelName ?? "World"),
    version: String(ver.Name ?? "unknown"),
    dataVersion: Number(d.DataVersion ?? ver.Id ?? 0),
    seed,
    spawn,
    modded: !!d.WasModded,
    brands: ((d.ServerBrands as string[]) ?? []).map(String),
    lastPlayed: d.LastPlayed as number | undefined,
    dayTime,
  };
}
